import { translate } from "components/string-table";
import { Colors, Icon, IconBlock, Section } from "main";

import styles from "./ClassroomPreferences.module.css";

type EditableClassroomPreferences =
  | {
      isEditable: false;
    }
  | {
      isEditable: true;
      onEditGradeLevel: () => void;
      onEditSubjects: () => void;
      onEditLanguages: () => void;
    };

// Types representing allowed values for grade levels, subjects, and languages
export type GradeLevelValueProps = (
  | "tk"
  | "pre-k"
  | "k"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10"
  | "11"
  | "12"
)[];
export type SubjectValueProps = (
  | "gen-ed"
  | "elective"
  | "english"
  | "physical-education"
  | "social-studies"
  | "math"
  | "science"
  | "special-education"
  | "world-languages"
  | "other"
  | "not-sure"
)[];
export type LanguageValueProps = (
  | "english"
  | "spanish"
  | "mandarin"
  | "cantonese"
  | "french"
  | "tagalog"
  | "vietnamese"
  | "arabic"
  | "korean"
  | "japanese"
  | "german"
  | "asl"
  | "other"
)[];

type ClassroomPreferences = {
  gradeLevels?: {
    id: number;
    value: GradeLevelValueProps;
  };
  subjects?: {
    id: number;
    value: SubjectValueProps;
  };
  languages?: {
    id: number;
    value: LanguageValueProps;
  };
};

// Mappings for converting grade levels between different formats
export const gradeLevelMappings = {
  datomic: {
    "k-2": "tk,pre-k,k,1,2",
    "3-5": "3,4,5",
    "6-8": "6,7,8",
    "9-12": "9,10,11,12",
  },
  valueTypes: {
    "pre-k": "k-2",
    tk: "k-2",
    k: "k-2",
    "1": "k-2",
    "2": "k-2",
    "3": "3-5",
    "4": "3-5",
    "5": "3-5",
    "6": "6-8",
    "7": "6-8",
    "8": "6-8",
    "9": "9-12",
    "10": "9-12",
    "11": "9-12",
    "12": "9-12",
  },
  display: {
    "pre-k": "Pre-K to 2nd",
    tk: "Pre-K to 2nd",
    k: "Pre-K to 2nd",
    "1": "Pre-K to 2nd",
    "2": "Pre-K to 2nd",
    "3": "3rd to 5th",
    "4": "3rd to 5th",
    "5": "3rd to 5th",
    "6": "6th to 8th",
    "7": "6th to 8th",
    "8": "6th to 8th",
    "9": "9th to 12th",
    "10": "9th to 12th",
    "11": "9th to 12th",
    "12": "9th to 12th",
  },
} as const;

// Mappings for converting subjects to their display formats
// Note: The order here defines how subjects will appear when displayed
export const subjectMapper = {
  "gen-ed": "General Education",
  elective: "Electives (Music, Art, etc.)",
  english: "English/Language Arts",
  "physical-education": "Health/Physical Education",
  "social-studies": "History/Social Studies",
  math: "Math",
  science: "Science",
  "special-education": "Special Education",
  "world-languages": "World Languages",
  other: "Other",
  "not-sure": "I'm not sure yet",
} as const;

// Mappings for converting languages to their display formats
// Note: The order here defines how languages will appear when displayed
export const languageMapper = {
  english: "English",
  spanish: "Spanish",
  mandarin: "Chinese (Mandarin)",
  cantonese: "Chinese (Cantonese)",
  french: "French",
  tagalog: "Tagalog",
  vietnamese: "Vietnamese",
  arabic: "Arabic",
  korean: "Korean",
  japanese: "Japanese",
  german: "German",
  asl: "American Sign Language",
  other: "Other",
} as const;

// Convert preferences to display-friendly format
export function convertPreferencesToDisplay(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valuesToConvert: any,
  conversionFormat:
    | typeof gradeLevelMappings.display
    | typeof gradeLevelMappings.valueTypes
    | typeof gradeLevelMappings.datomic
    | typeof subjectMapper
    | typeof languageMapper,
) {
  if (!valuesToConvert || valuesToConvert.length === 0) return undefined;

  const convertedValues = Array.from(
    new Set(valuesToConvert.map((v: keyof typeof conversionFormat) => conversionFormat[v])),
  ) as string[];

  // Sort Grades based on display grades order
  if (conversionFormat === gradeLevelMappings.display) {
    return convertedValues.sort((a, b) => getGradeLevelIndex(a) - getGradeLevelIndex(b));
  }

  // Sort subjects and languages in the order set by
  // convertSubjects and convertLanguages
  const predefinedOrder = Object.values(conversionFormat) as string[];
  const predefinedOrderIndexMap = predefinedOrder.reduce(
    (indexMap, item, idx) => {
      indexMap[item] = idx;
      return indexMap;
    },
    {} as Record<string, number>,
  );

  return convertedValues.sort((a, b) => predefinedOrderIndexMap[a] - predefinedOrderIndexMap[b]);
}

// Convert preferences into a savable format
export function convertPreferencesToSave(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valuesToConvert: any,
  conversionFormat:
    | typeof gradeLevelMappings.display
    | typeof gradeLevelMappings.valueTypes
    | typeof gradeLevelMappings.datomic
    | typeof subjectMapper
    | typeof languageMapper,
) {
  // Handle grade-level conversion to the datomic format
  if (conversionFormat === gradeLevelMappings.datomic) {
    return valuesToConvert.map((grade: keyof typeof conversionFormat) => conversionFormat[grade]);
  }
  return JSON.parse(valuesToConvert).map((v: keyof typeof conversionFormat) => v);
}

export type ClassroomPreferencesProps = {
  editable?: EditableClassroomPreferences;
  classroomPreferences?: ClassroomPreferences;
};

export function ClassroomPreferences(props: ClassroomPreferencesProps) {
  const { editable, classroomPreferences } = props;

  return (
    <Section title={translate("SUB_PROFILE_CLASSROOM_PREFERENCES_HEADER")}>
      <div className={styles.container}>
        <IconBlock
          content={
            <div>
              <span className={styles["content-title"]}>
                {translate("SUB_PROFILE_CLASSROOM_PREFERENCES_LABEL_GRADE_LEVEL")}:{" "}
              </span>
              {renderContent(
                convertPreferencesToDisplay(
                  classroomPreferences?.gradeLevels?.value,
                  gradeLevelMappings.display,
                ),
              )}
            </div>
          }
          leftIcon={<Icon name="Grad Cap" />}
          {...(editable?.isEditable
            ? {
                rightIcon: {
                  icon: <Icon name="Edit" color={Colors.blue500} />,
                  onClick: editable.onEditGradeLevel,
                },
              }
            : {})}
        />
        <IconBlock
          content={
            <div>
              <span className={styles["content-title"]}>
                {translate("SUB_PROFILE_CLASSROOM_PREFERENCES_LABEL_SUBJECTS")}:{" "}
              </span>
              {renderContent(
                convertPreferencesToDisplay(classroomPreferences?.subjects?.value, subjectMapper),
              )}
            </div>
          }
          leftIcon={<Icon name="Book" />}
          {...(editable?.isEditable
            ? {
                rightIcon: {
                  icon: <Icon name="Edit" color={Colors.blue500} />,
                  onClick: editable.onEditSubjects,
                },
              }
            : {})}
        />
        <IconBlock
          content={
            <div>
              <span className={styles["content-title"]}>
                {translate("SUB_PROFILE_CLASSROOM_PREFERENCES_LABEL_LANGUAGES")}:{" "}
              </span>
              {renderContent(
                convertPreferencesToDisplay(classroomPreferences?.languages?.value, languageMapper),
              )}
            </div>
          }
          leftIcon={<Icon name="Chat Bubble Active" />}
          {...(editable?.isEditable
            ? {
                rightIcon: {
                  icon: <Icon name="Edit" color={Colors.blue500} />,
                  onClick: editable.onEditLanguages,
                },
              }
            : {})}
        />
      </div>
    </Section>
  );
}

function renderContent(content?: string[]) {
  if (!content) {
    return (
      <span className={styles["content-no-info"]}>{translate("SUB_PROFILE_NO_INFORMATION")}</span>
    );
  }
  return <span>{content.join(", ")}</span>;
}

// Gets the index of a grade level for sorting
export function getGradeLevelIndex(value: string): number {
  const gradeKeys = Object.keys(gradeLevelMappings.display);
  return gradeKeys.indexOf(
    Object.keys(gradeLevelMappings.display).find(
      (k) => gradeLevelMappings.display[k as keyof typeof gradeLevelMappings.display] === value,
    ) ?? "",
  );
}
